import type { AccountOption } from "@app/components/account-combobox";
import KpiChart from "@app/components/kpi-chart";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import {
  DATE_RANGE_LABELS,
  dateRangeFromISO,
  useDateRange,
} from "@app/lib/date-range";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useEffect, useState } from "react";
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay";

import type {
  categoryDetailPanel_Query,
  categoryDetailPanel_Query$data,
} from "@app/queries/categoryDetailPanel_Query.graphql";
import type { categoryDetailPanelTxns_Query } from "@app/queries/categoryDetailPanelTxns_Query.graphql";

const detailQuery = graphql`
  query categoryDetailPanel_Query(
    $categoryId: ID!
    $perspectiveId: ID
    $from: String
    $to: String
  ) {
    categoryDetail(
      categoryId: $categoryId
      perspectiveId: $perspectiveId
      from: $from
      to: $to
    ) {
      path
      total
      breakdown {
        stakeholder {
          id
          name
        }
        amount
        monthly {
          month
          value
        }
      }
      totalCount
      limit
      txns {
        postingDate
        description
        amount
        internal
        accountNumber
        counterparty {
          id
          name
        }
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

// The editable transactions shell pulls this category subtree's txns straight off
// the unified `transactions` resolver (categoryId drill), independent of the chrome
// query above, plus the option lists the shared bulk-action registry needs.
const txnsQuery = graphql`
  query categoryDetailPanelTxns_Query(
    $categoryId: ID
    $perspectiveId: ID
    $from: String
    $to: String
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      categoryId: $categoryId
      perspectiveId: $perspectiveId
      from: $from
      to: $to
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

type Detail = NonNullable<categoryDetailPanel_Query$data["categoryDetail"]>;

// Stack colors for the top counterparties; the rest fold into a grey "Other".
const PALETTE = [
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#f43f5e",
  "#14b8a6",
  "#f97316",
  "#6366f1",
];
const OTHER_COLOR = "#cbd5e1";
const TOP_N = 8;

const money0 = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
export default function CategoryDetailPanel({
  category,
  perspectiveId,
  onClose,
  onOpenStakeholder,
  from,
  to,
}: {
  category: { id: string; name: string; color: string };
  perspectiveId: string | null;
  onClose: () => void;
  onOpenStakeholder?: (stakeholderId: string) => void;
  // When set (overview grid cell), scope to exactly this window and ignore the
  // global date lens. Omitted on the categorize page → global range as before.
  from?: string;
  to?: string;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  // Bumped after an editable-shell bulk action so both the shell sub-query and this
  // chrome query re-fetch and the breakdown reconciles with the edit.
  const [reloadKey, setReloadKey] = useState(0);
  const range = useDateRange();
  const scoped = Boolean(from && to);
  const fromArg = from ?? dateRangeFromISO(range) ?? undefined;
  const toArg = to ?? undefined;

  useEffect(() => {
    setDetail(null);
    const subscription = fetchQuery<categoryDetailPanel_Query>(
      environment,
      detailQuery,
      {
        categoryId: category.id,
        perspectiveId,
        from: fromArg,
        to: toArg,
      }
    ).subscribe({
      next: (data) => setDetail(data.categoryDetail ?? null),
    });
    return () => subscription.unsubscribe();
  }, [category.id, perspectiveId, fromArg, toArg, reloadKey]);

  const breakdown = detail?.breakdown ?? [];
  const path = detail?.path ?? [category.name];

  // Stacked monthly histogram: one bar series per top counterparty (plus an
  // "Other" roll-up), all sharing the union of months in the selected range.
  const months = Array.from(
    new Set(breakdown.flatMap((s) => s.monthly.map((p) => p.month)))
  ).sort();
  const top = breakdown.slice(0, TOP_N);
  const rest = breakdown.slice(TOP_N);
  const pointsFor = (
    monthly: ReadonlyArray<{
      readonly month: string;
      readonly value: number | null | undefined;
    }>
  ) => {
    const byMonth = new Map(monthly.map((p) => [p.month, p.value ?? 0]));
    return months.map((month) => ({ month, value: byMonth.get(month) ?? 0 }));
  };
  const series = top.map((share, index) => ({
    name: share.stakeholder.name,
    type: "bar" as const,
    stack: "cost",
    color: PALETTE[index % PALETTE.length],
    points: pointsFor(share.monthly),
  }));
  if (rest.length > 0) {
    const otherByMonth = new Map<string, number>();
    for (const share of rest) {
      for (const point of share.monthly) {
        otherByMonth.set(
          point.month,
          (otherByMonth.get(point.month) ?? 0) + (point.value ?? 0)
        );
      }
    }
    series.push({
      name: `Other (${rest.length})`,
      type: "bar" as const,
      stack: "cost",
      color: OTHER_COLOR,
      points: months.map((month) => ({
        month,
        value: otherByMonth.get(month) ?? 0,
      })),
    });
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className="fixed right-0 top-0 h-full w-[680px] max-w-[92vw] bg-white shadow-2xl z-50 flex flex-col focus:outline-none">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <nav className="text-xs text-gray-400 mb-1 flex flex-wrap items-center gap-1">
                  {path.map((name, index) => (
                    <span key={index} className="flex items-center gap-1">
                      {index > 0 && <span className="text-gray-300">→</span>}
                      <span
                        className={
                          index === path.length - 1
                            ? "text-gray-600 font-medium"
                            : ""
                        }
                      >
                        {name}
                      </span>
                    </span>
                  ))}
                </nav>
                <Dialog.Title className="text-lg font-bold text-gray-900 truncate flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: category.color }}
                  />
                  {category.name}
                </Dialog.Title>
                <div className="text-sm text-gray-500">
                  total cost {money0(detail?.total ?? 0)}
                  <span className="text-gray-400">
                    {" "}
                    ·{" "}
                    {scoped && from
                      ? new Date(`${from}T00:00:00`).toLocaleDateString("en-US", {
                          month: "short",
                          year: "numeric",
                        })
                      : DATE_RANGE_LABELS[range].toLowerCase()}
                  </span>
                </div>
              </div>
              <Dialog.Close className="shrink-0 text-2xl leading-none text-gray-400 hover:text-gray-700 px-1">
                ×
              </Dialog.Close>
            </div>
          </div>

          {detail === null ? (
            <div className="p-6 text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              <section>
                <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
                  Monthly cost by counterparty
                </h3>
                {series.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    No spend in this period.
                  </p>
                ) : (
                  <>
                    <KpiChart
                      title=""
                      series={series}
                      format="money"
                      onMonthClick={() => undefined}
                    />
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                      {top.map((share, index) => (
                        <button
                          key={share.stakeholder.id}
                          type="button"
                          onClick={() =>
                            onOpenStakeholder?.(share.stakeholder.id)
                          }
                          title="Open counterparty details"
                          className="flex items-center gap-1.5 text-xs text-gray-600 hover:underline"
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-sm shrink-0"
                            style={{
                              backgroundColor: PALETTE[index % PALETTE.length],
                            }}
                          />
                          <span className="truncate max-w-[10rem]">
                            {share.stakeholder.name}
                          </span>
                          <span className="tabular-nums text-gray-400">
                            {money0(share.amount)}
                          </span>
                        </button>
                      ))}
                      {rest.length > 0 && (
                        <span className="flex items-center gap-1.5 text-xs text-gray-400">
                          <span
                            className="w-2.5 h-2.5 rounded-sm shrink-0"
                            style={{ backgroundColor: OTHER_COLOR }}
                          />
                          Other ({rest.length})
                        </span>
                      )}
                    </div>
                  </>
                )}
              </section>

              <section>
                <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
                  Transactions
                </h3>
                <div className="bg-white rounded border overflow-hidden">
                  <Suspense
                    fallback={
                      <p className="p-6 text-sm text-gray-400">
                        Loading transactions…
                      </p>
                    }
                  >
                    <CategoryTxns
                      categoryId={category.id}
                      from={fromArg}
                      to={toArg}
                      onChanged={() => setReloadKey((key) => key + 1)}
                    />
                  </Suspense>
                </div>
              </section>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// The editable transactions list for this category subtree, fed by its own
// `transactions(categoryId: …)` query and wired to the shared bulk-action registry.
function CategoryTxns({
  categoryId,
  from,
  to,
  onChanged,
}: {
  categoryId: string;
  from: string | undefined;
  to: string | undefined;
  onChanged: () => void;
}) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<categoryDetailPanelTxns_Query>(
    txnsQuery,
    {
      categoryId,
      perspectiveId: getPerspectiveId() ?? undefined,
      from,
      to,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] =
    data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => {
      refresh();
      onChanged();
    },
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  const exportParams: Record<string, string> = {
    category_id: categoryId,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        mode="inline"
        interaction="editable"
        title="Transactions"
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No transactions in this period."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        renderBulkActions={renderBulkActions}
      />
      {dialogs}
    </>
  );
}
